import { useContext, useEffect, useState } from 'react'

import Grid from '@mui/material/Grid2'
import Typography from '@mui/material/Typography'

import { Divider } from '@mui/material'
import { map } from 'lodash'
import { Completion, File } from '../../../api'
import { AppStateContext } from '../../../state/AppProvider'
import { ToastMessage } from '../../ExperimentForm/ExperimentFormToast'
import { BinaryRadioFormField } from '../../form/BinaryRadioFormField'
import { FileInputFormField } from '../../form/FileInputFormField'
import { NumberFormField } from '../../form/NumberFormField'
import { SelectFormField } from '../../form/SelectFormField'
import { TextFormField } from '../../form/TextFormField'
import styles from './AnalysisAndResultsStep.module.css'

interface Props {
  onError?: (message: Pick<ToastMessage, 'title' | 'description' | 'type'>) => void
}

export const AnalysisAndResultsStep = ({ onError }: Props) => {
  const appStateContext = useContext(AppStateContext)
  const [data, setData] = useState<Completion | null>(null)
  const [convId, setConvId] = useState<string>('')
  const [files, setFiles] = useState<(File & { fieldName: string })[]>([])

  // Retrieve the current chat data
  useEffect(() => {
    if (appStateContext?.state.currentChat?.extended_data?.experiment?.completion) {
      let contextData = appStateContext?.state.currentChat?.extended_data?.experiment?.completion

      setConvId(appStateContext?.state.currentChat?.id || '')
      setData(contextData)

      const upload_result_deck = contextData.upload_result_deck.field_value ?? []
      const other_supporting_data = contextData.other_supporting_data.field_value ?? []

      setFiles([
        ...map(upload_result_deck, file => ({ ...file, fieldName: 'upload_result_deck' })),
        ...map(other_supporting_data, file => ({
          ...file,
          fieldName: 'other_supporting_data',
        })),
      ])
    }
  }, [appStateContext?.state.currentChat?.extended_data])

  if (!data) {
    return <Typography>Loading ... or Error</Typography>
  }

  return (
    <Grid
      container
      columnSpacing={2}
      rowSpacing={4}>
      <BinaryRadioFormField
        stepName="completion"
        fieldName="hypothesis_proven"
        convId={convId}
        name="Hypothesis Proven?"
        gridSize={6}
        data={data?.hypothesis_proven}
        onError={onError}
        disabled={data?.hypothesis_proven?.disabled}
      />

      <SelectFormField
        name="Confidence in Results"
        stepName="completion"
        fieldName="confidence_in_results"
        convId={convId}
        gridSize={6}
        data={data?.confidence_in_results}
        onError={onError}
        disabled={data?.confidence_in_results?.disabled}
      />
      <NumberFormField
        name="Total Experiment DME Spend (USD)"
        stepName="completion"
        fieldName="total_experiment_dme_spend"
        convId={convId}
        gridSize={12}
        data={data?.total_experiment_dme_spend}
        onError={onError}
        disabled={data?.total_experiment_dme_spend?.disabled}
      />

      <TextFormField
        name="Additional Comments"
        stepName="completion"
        fieldName="additional_comments"
        convId={convId}
        data={data?.additional_comments}
        gridSize={12}
        multiline
        rows={4}
        onError={onError}
        disabled={data?.additional_comments?.disabled}
      />

      <Grid size={12}>
        <Divider className={styles.divider} />
        <Typography
          className={styles.sectionTitle}
          variant="h6">
          File Upload
        </Typography>
      </Grid>

      <Grid size={12}>
        <ul className={styles.infoList}>
          <li>Ensure that all mandatory files are included before submission to avoid delays</li>
          <li>Please refer to the templates in order to properly set and upload your files</li>
          <li>If you have any questions about which files to upload, please contact support</li>
        </ul>
      </Grid>

      <Grid
        size={12}
        gap={5}
        display={'flex'}
        flexDirection={'column'}>
        <FileInputFormField
          data={data.upload_result_deck}
          name="1. Result Deck"
          stepName="completion"
          convId={convId}
          fieldName="upload_result_deck"
          templateUrl={appStateContext?.state.frontendSettings?.template?.upload_result_deck}
          onError={onError}
        />
        <FileInputFormField
          data={data.other_supporting_data}
          name="2. Other Supporting Data"
          stepName="completion"
          convId={convId}
          fieldName="other_supporting_data"
          subtitle="Include raw data and calculations behind the final result"
          onError={onError}
        />
      </Grid>
    </Grid>
  )
}
